@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.certificates

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.probes.PdfBlockedNotice
import com.nonopn.skydome.common.app.pages.probes.rememberProbeAccess
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryType
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.PulseType
import com.nonopn.skydome.common.app.pages.sharing.ShareManager
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.cert_foudr_title
import com.nonopn.skywatcher.common.app.res.cert_incidence_direct_indirect
import com.nonopn.skywatcher.common.app.res.cert_incidence_indirect_only
import com.nonopn.skywatcher.common.app.res.cert_incidence_unlikely
import com.nonopn.skywatcher.common.app.res.doc_export_pdf
import com.nonopn.skywatcher.common.app.res.label_latitude
import com.nonopn.skywatcher.common.app.res.label_longitude
import com.nonopn.skywatcher.common.app.res.strike_date
import com.nonopn.skywatcher.common.app.res.strike_distance
import com.nonopn.skywatcher.common.app.res.strike_incidence_probability
import com.nonopn.skywatcher.common.app.res.strike_intensity
import com.nonopn.skywatcher.common.app.res.strike_sensors
import com.nonopn.skywatcher.common.app.res.strike_time_utc
import com.nonopn.skywatcher.design.utils.localized
import org.jetbrains.compose.resources.stringResource
import com.nonopn.skywatcher.maps.MapUtils

import com.nonopn.skydome.common.app.utils.fmt
import com.nonopn.skydome.common.app.utils.pad2
import korlibs.time.DateTime

import kotlin.math.absoluteValue
import kotlin.uuid.ExperimentalUuidApi

private object Colors {
    val Primary = Color(0xFF2B7FE0)
    val Text = Color(0xFF1A2B4A)
    val Card = Color(0xFFEEF2F8)
    val Muted = Color(0xFF9AADC0)
    val SecondaryText = Color(0xFF6B7A99)
    val Positive = Color(0xFFE63B2E) // red = CG+ (positive)
    val Negative = Color(0xFF2B7FE0) // blue = CG- (negative)
}

@Suppress("LongMethod", "MagicNumber")
@OptIn(ExperimentalUuidApi::class)
@Composable
fun RenderThunderDetectionActivity(
    modifier: Modifier = Modifier,
    project: Project,
    lightningDetectionActivity: ThunderDetectionActivityWithIncidence,
    shareManager: ShareManager,
    appModel: AppModel
) {
    val (activity, incidence) = lightningDetectionActivity
    val isCG = activity.pulseType == PulseType.CG
    val isPositive = activity.pulsePeakAmp > 0
    val polarityColor = if (isPositive) Colors.Positive else Colors.Negative
    val polarityLabel = if (isPositive) "CG+" else "CG−"
    val intensityKa = (activity.pulsePeakAmp.toDouble().absoluteValue / 1000.0).fmt(2)
    val distanceKm = MapUtils.distance(
        project.latitude,
        project.longitude,
        activity.latitude,
        activity.longitude
    ).fmt(2)
    val certTitle = stringResource(Res.string.cert_foudr_title, project.name)
    val labelDirectIndirect = stringResource(Res.string.cert_incidence_direct_indirect)
    val labelIndirectOnly = stringResource(Res.string.cert_incidence_indirect_only)
    val labelUnlikely = stringResource(Res.string.cert_incidence_unlikely)

    Card(
        modifier = modifier.padding(4.dp).fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(8.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            // Line 1: GLD360+ ID + polarity badge
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(Modifier.weight(1f)) {
                    Text(
                        "ID GLD360+",
                        fontSize = 9.sp,
                        color = Colors.Muted,
                        fontWeight = FontWeight.Medium
                    )
                    Text(
                        activity.externalId.ifEmpty { "—" },
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Colors.Text,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                if (isCG) {
                    Box(
                        Modifier.clip(RoundedCornerShape(20.dp))
                            .background(polarityColor.copy(alpha = 0.12f))
                            .padding(horizontal = 12.dp, vertical = 4.dp)
                    ) {
                        Text(
                            polarityLabel,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = polarityColor
                        )
                    }
                }
            }

            HorizontalDivider(color = Colors.Card)

            // Line 2: Date + Time
            activity.recDateTime.let { dt ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                    LabelValue(
                        label = stringResource(Res.string.strike_date),
                        value = "${dt.dayOfMonth.pad2()}/${dt.month1.pad2()}/${dt.yearInt}"
                    )
                    LabelValue(
                        label = stringResource(Res.string.strike_time_utc),
                        value = "${dt.hours.pad2()}h${dt.minutes.pad2()}:${dt.seconds.pad2()}s"
                    )
                }
            }

            // Line 3: Intensity + Distance + Sensors
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                KpiBox(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.strike_intensity),
                    value = "$intensityKa",
                    unit = "kA",
                    color = polarityColor
                )
                KpiBox(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.strike_distance),
                    value = distanceKm,
                    unit = "km",
                    color = Colors.Primary
                )
                KpiBox(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.strike_sensors),
                    value = "${activity.pulseNumSensors}",
                    unit = "stations",
                    color = Colors.Text
                )
            }

            // Line 4: GPS coordinates
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                LabelValue(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.label_latitude),
                    value = "${activity.latitude.fmt(5)}°"
                )
                LabelValue(
                    modifier = Modifier.weight(1f),
                    label = stringResource(Res.string.label_longitude),
                    value = "${activity.longitude.fmt(5)}°"
                )
            }

            // PDF exports need a paid probe when only trial probes cover the site
            val probeAccess = rememberProbeAccess(appModel, project.latitude, project.longitude)

            // Incidence probability + PDF button
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(stringResource(Res.string.strike_incidence_probability), fontSize = 10.sp, color = Colors.Muted)
                    Text(
                        "${(incidence.preciseProbability * 100).fmt(1)} %",
                        fontSize = 18.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = Colors.Primary
                    )
                }
                OutlinedButton(
                    enabled = !probeAccess.pdfBlocked,
                    onClick = {
                        val nowMillis = DateTime.now().unixMillisLong
                        shareManager.shareHtmlAsPdf(
                            certTitle,
                            buildLightningCertificateExportHtml(
                                project, lightningDetectionActivity, certTitle,
                                labelDirectIndirect, labelIndirectOnly, labelUnlikely
                            )
                        )
                        appModel.recordDocumentHistory(
                            DocumentHistoryItem(
                                id = "cert_lightning_${project.uuid}_$nowMillis",
                                type = DocumentHistoryType.CERT_LIGHTNING,
                                siteName = project.name,
                                latitude = project.latitude,
                                longitude = project.longitude,
                                createdAtSecondsUtc = nowMillis / 1000L,
                            )
                        )
                    },
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Colors.Primary)
                ) {
                    Text(Res.string.doc_export_pdf.localized(), fontSize = 12.sp)
                }
            }
            if (probeAccess.pdfBlocked) {
                PdfBlockedNotice()
            }
        }
    }
}

private fun buildLightningCertificateExportHtml(
    project: Project,
    lightningDetectionActivity: ThunderDetectionActivityWithIncidence,
    title: String,
    labelDirectIndirect: String,
    labelIndirectOnly: String,
    labelUnlikely: String
): String {
    val (activity, incidence) = lightningDetectionActivity
    val dt = activity.recDateTime
    val intensityKa = (activity.pulsePeakAmp.toDouble().absoluteValue / 1000.0).fmt(2)
    val probabilityPercent = incidence.preciseProbability * 100
    val probabilityLabel = probabilityPercent.fmt(1)
    val probabilityRounded = probabilityPercent
    val distanceKm = MapUtils.distance(
        project.latitude,
        project.longitude,
        activity.latitude,
        activity.longitude
    )
    val polarityLabel = if (activity.pulsePeakAmp > 0) "CG+" else "CG−"
    val danger = probabilityRounded.toDangerLevel(labelDirectIndirect, labelIndirectOnly, labelUnlikely)
    val impactDate = "${dt.dayOfMonth.pad2()}/${dt.month1.pad2()}/${dt.yearInt}"
    val impactTime = "${dt.hours.pad2()}h${dt.minutes.pad2()}:${dt.seconds.pad2()}s"
    val impactCoordinates = "${activity.latitude.fmt(5)}°, ${activity.longitude.fmt(5)}°"
    val siteCoordinates = "${project.latitude.fmt(5)}°, ${project.longitude.fmt(5)}°"
    val impactId = activity.externalId.ifEmpty { "—" }.escapeHtml()
    return """
        <!doctype html>
        <html lang="fr">
        <head>
          <meta charset="utf-8">
          <title>${title.escapeHtml()}</title>
          <style>
            @page { size: A4; margin: 18mm; }
            body { font-family: Arial, sans-serif; color: #1A2B4A; font-size: 15px; }
            h1 { color: #2B7FE0; font-size: 24px; margin-bottom: 6px; }
            h2 { font-size: 17px; margin-top: 22px; }
            .muted { color: #6B7A99; font-size: 13px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
            .card { border: 1px solid #C5D9F5; border-radius: 12px; padding: 14px; margin: 14px 0; }
            .label { color: #6B7A99; font-size: 13px; }
            .value { font-size: 22px; font-weight: 700; margin-top: 4px; }
            .danger { background: ${danger.background}; border-color: ${danger.border}; }
            .danger .value { color: ${danger.text}; }
            table { border-collapse: collapse; width: 100%; margin-top: 8px; }
            td { border-bottom: 1px solid #EEF2F8; padding: 8px 4px; vertical-align: top; }
            td:first-child { color: #6B7A99; width: 42%; }
            .note { background: #F5F7FA; border-radius: 10px; padding: 12px; }
          </style>
        </head>
        <body>
          <h1>${title.escapeHtml()}</h1>
          <div class="muted">Site : ${project.name.escapeHtml()}</div>
          <section class="card danger">
            <div class="label">Évaluation danger</div>
            <div class="value">${danger.label.escapeHtml()}</div>
            <p>Probabilité d'incidence : $probabilityLabel %</p>
          </section>
          <section class="grid">
            <div class="card">
              <div class="label">Impact détecté</div>
              <div class="value">$polarityLabel · $intensityKa kA</div>
            </div>
            <div class="card">
              <div class="label">Distance au site</div>
              <div class="value">${distanceKm.fmt(2)} km</div>
            </div>
          </section>
          <h2>Détails de l'impact</h2>
          <table>
            <tr><td>Date</td><td>$impactDate</td></tr>
            <tr><td>Heure UTC</td><td>$impactTime</td></tr>
            <tr><td>Réseau de détection</td><td>GLD360+</td></tr>
            <tr><td>ID d'impact</td><td>$impactId</td></tr>
            <tr><td>Coordonnées impact</td><td>$impactCoordinates</td></tr>
            <tr><td>Coordonnées site</td><td>$siteCoordinates</td></tr>
            <tr><td>Capteurs</td><td>${activity.pulseNumSensors} stations</td></tr>
          </table>
          <h2>Interprétation</h2>
          <div class="note">
            La probabilité d'incidence estime la probabilité que ce coup de foudre ait eu une
            incidence directe ou indirecte sur le point étudié. L'analyse tient compte de la
            distance, de l'ellipse de localisation, de l'intensité et de la polarité.
          </div>
          <p class="muted">IEC 62305-2:2024 · IEC 62858:2019 · NF C 17-102:2011</p>
        </body>
        </html>
    """.trimIndent()
}

private data class DangerLevel(
    val label: String,
    val background: String,
    val border: String,
    val text: String
)

private fun Double.toDangerLevel(
    labelDirectIndirect: String,
    labelIndirectOnly: String,
    labelUnlikely: String
): DangerLevel = when {
    this >= 80.0 -> DangerLevel(
        label = labelDirectIndirect,
        background = "#FFF0F0",
        border = "#E63B2E",
        text = "#B00020"
    )
    this >= 30.0 -> DangerLevel(
        label = labelIndirectOnly,
        background = "#FFF3E0",
        border = "#F5A800",
        text = "#E65100"
    )
    else -> DangerLevel(
        label = labelUnlikely,
        background = "#E8F5E9",
        border = "#2E7D32",
        text = "#2E7D32"
    )
}

private fun String.escapeHtml(): String = replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

@Composable
private fun LabelValue(modifier: Modifier = Modifier, label: String, value: String) {
    Column(modifier) {
        Text(label, fontSize = 9.sp, color = Colors.Muted, fontWeight = FontWeight.Medium)
        Text(value, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Colors.Text)
    }
}

@Composable
private fun KpiBox(
    modifier: Modifier = Modifier,
    label: String,
    value: String,
    unit: String,
    color: Color
) {
    Column(
        modifier.clip(RoundedCornerShape(6.dp)).background(Colors.Card).padding(8.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(value, fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = color)
        Text(unit, fontSize = 8.sp, color = Colors.Muted)
        Text(label, fontSize = 9.sp, color = Colors.SecondaryText, fontWeight = FontWeight.Medium)
    }
}
